// Pipeline Execution Panel
// Real-time monitoring and controls for pipeline execution

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Play,
  Square,
  RefreshCw,
  FileText,
  Database,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Copy,
  Download,
} from 'lucide-react';
import { PipelineRunStatus } from '../types/pipeline';
import { useExecutePipeline, usePipelineStatus, usePipelineData } from '../hooks/use-pipelines';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface PipelineExecutionPanelProps {
  pipelineId: string;
  isActive: boolean;
}

export default function PipelineExecutionPanel({
  pipelineId,
  isActive,
}: PipelineExecutionPanelProps) {
  const { data: statusData, isLoading: statusLoading } = usePipelineStatus(pipelineId);
  const { data: dataData } = usePipelineData(pipelineId);
  const executePipeline = useExecutePipeline();
  const [activeTab, setActiveTab] = useState<'status' | 'history' | 'data'>('status');
  const [isExporting, setIsExporting] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState<Set<string>>(new Set());

  const currentRun = statusData?.currentRun;
  const recentRuns = statusData?.recentRuns || [];

  const getStatusIcon = (status: PipelineRunStatus) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4" />;
      case 'RUNNING':
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'COMPLETED':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'CANCELLED':
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusBadgeVariant = (status: PipelineRunStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'RUNNING':
        return 'default';
      case 'COMPLETED':
        return 'default';
      case 'FAILED':
        return 'destructive';
      case 'CANCELLED':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <Card className="h-full overflow-auto">
      <CardHeader>
        <CardTitle>Pipeline Execution</CardTitle>
        <CardDescription>
          {isActive ? 'Pipeline is active and ready to execute' : 'Pipeline is inactive'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Execute Button */}
        <div className="flex gap-2">
          <Button
            onClick={() => executePipeline.mutate(pipelineId)}
            disabled={!isActive || executePipeline.isPending || (currentRun?.status === 'RUNNING')}
            className="flex-1"
          >
            {executePipeline.isPending ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : currentRun?.status === 'RUNNING' ? (
              <>
                <Square className="mr-2 h-4 w-4" />
                Running...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Execute Pipeline
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => statusData && statusData.refetch()}>
            <RefreshCw className={`mr-2 h-4 w-4 ${statusLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Current Run Status */}
        {currentRun && (
          <Card className="border-muted">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(currentRun.status)}
                  <span className="font-medium">Current Run</span>
                </div>
                <Badge variant={getStatusBadgeVariant(currentRun.status)}>
                  {currentRun.status}
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Started</p>
                  <p className="font-medium">
                    {formatDistanceToNow(new Date(currentRun.startedAt), { addSuffix: true })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Records</p>
                  <p className="font-medium">
                    {currentRun.recordsProcessed || 0}
                    {currentRun.recordsFailed && currentRun.recordsFailed > 0 && (
                      <span className="text-red-600"> ({currentRun.recordsFailed} failed)</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p className="font-medium">{formatDuration(currentRun.duration)}</p>
                </div>
              </div>

              {currentRun.errorMessage && (
                <div className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <p className="font-medium">Error:</p>
                  <p>{currentRun.errorMessage}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
          <TabsList className="w-full">
            <TabsTrigger value="status" className="flex-1">
              <FileText className="mr-2 h-4 w-4" />
              Status
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1">
              <Clock className="mr-2 h-4 w-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="data" className="flex-1">
              <Database className="mr-2 h-4 w-4" />
              Data
            </TabsTrigger>
          </TabsList>

          {/* Status Tab */}
          <TabsContent value="status" className="mt-4">
            {statusData ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Last Run</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {statusData.lastRunAt ? (
                        <div className="text-sm">
                          <p className="text-muted-foreground">Time:</p>
                          <p className="font-medium">
                            {formatDistanceToNow(new Date(statusData.lastRunAt), { addSuffix: true })}
                          </p>
                          {statusData.lastRunStatus && (
                            <Badge
                              variant={
                                statusData.lastRunStatus === 'SUCCESS'
                                  ? 'default'
                                  : statusData.lastRunStatus === 'FAILED'
                                    ? 'destructive'
                                    : 'secondary'
                              }
                              className="mt-2"
                            >
                              {statusData.lastRunStatus}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Never executed</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Next Run</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {statusData.nextRunAt ? (
                        <div className="text-sm">
                          <p className="text-muted-foreground">Scheduled:</p>
                          <p className="font-medium">
                            {formatDistanceToNow(new Date(statusData.nextRunAt), { addSuffix: true })}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {statusData.isActive ? 'No schedule configured' : 'Pipeline inactive'}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Pipeline Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Name:</span>
                        <span className="font-medium">{statusData.pipelineName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge variant={isActive ? 'default' : 'secondary'}>
                          {isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center text-sm text-muted-foreground py-4">
                Loading status...
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-4">
            <div className="space-y-2">
              {recentRuns.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  No execution history yet
                </div>
              ) : (
                recentRuns.map((run) => (
                  <Card key={run.runId}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(run.status)}
                          <span className="text-sm font-medium">Run ID: {run.runId.slice(0, 8)}</span>
                        </div>
                        <Badge variant={getStatusBadgeVariant(run.status)}>{run.status}</Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Started:</span>
                          <span className="block font-medium">
                            {formatDistanceToNow(new Date(run.startedAt), { addSuffix: true })}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="block font-medium">{formatDuration(run.duration)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Records:</span>
                          <span className="block font-medium">
                            {run.recordsProcessed || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Result:</span>
                          <span className="block font-medium">
                            {run.recordsSuccess} successful
                            {run.recordsFailed && run.recordsFailed > 0 && (
                              <span className="text-red-600">, {run.recordsFailed} failed</span>
                            )}
                          </span>
                        </div>
                      </div>
                      {run.errorMessage && (
                        <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                          {run.errorMessage}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Data Tab */}
          <TabsContent value="data" className="mt-4">
            {dataData && dataData.data && dataData.data.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold">Transformed Data</h3>
                    <p className="text-xs text-muted-foreground">
                      {dataData.totalRecords || 0} records from {dataData.datasetCount || 0} dataset(s)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isExporting}
                      onClick={() => {
                        const baseUrl = import.meta.env.VITE_API_URL || '/api/v1';
                        const token = localStorage.getItem('accessToken');
                        const totalRecords = dataData.totalRecords || 0;

                        setIsExporting(true);

                        const selectedCount = selectedDatasets.size > 0
                          ? dataData.schemas?.filter((s: any) => selectedDatasets.has(s.id)).reduce((sum: number, s: any) => sum + s.recordCount, 0) || 0
                          : totalRecords;
                        const toastId = toast.loading(
                          `Exporting ${selectedCount.toLocaleString()} records to Excel...`,
                          { duration: Infinity }
                        );

                        const exportUrl = selectedDatasets.size > 0
                          ? `${baseUrl}/pipelines/${pipelineId}/export?datasetIds=${Array.from(selectedDatasets).join(',')}`
                          : `${baseUrl}/pipelines/${pipelineId}/export`;

                        fetch(exportUrl, {
                          headers: token ? { Authorization: `Bearer ${token}` } : {},
                        })
                          .then((res) => {
                            if (!res.ok) throw new Error('Export failed');
                            return res.blob();
                          })
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `pipeline-${pipelineId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.xlsx`;
                            a.click();
                            URL.revokeObjectURL(url);

                            const sizeMB = (blob.size / 1024 / 1024).toFixed(1);
                            toast.success(
                              `Exported ${totalRecords.toLocaleString()} records (${sizeMB} MB)`,
                              { id: toastId }
                            );
                          })
                          .catch(() => toast.error('Failed to export Excel', { id: toastId }))
                          .finally(() => setIsExporting(false));
                      }}
                    >
                      {isExporting ? (
                        <RefreshCw className="mr-2 h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-3 w-3" />
                      )}
                      {isExporting ? 'Exporting...' : 'Export Excel'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(dataData.data, null, 2));
                        toast.success('JSON copied to clipboard');
                      }}
                    >
                      <Copy className="mr-2 h-3 w-3" />
                      Copy JSON
                    </Button>
                  </div>
                </div>

                {dataData.schemas?.map((schema: any) => (
                  <Card key={schema.id || schema.tableName} className={selectedDatasets.has(schema.id) ? 'ring-2 ring-primary' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedDatasets.has(schema.id)}
                          onChange={(e) => {
                            const next = new Set(selectedDatasets);
                            if (e.target.checked) {
                              next.add(schema.id);
                            } else {
                              next.delete(schema.id);
                            }
                            setSelectedDatasets(next);
                          }}
                          className="h-4 w-4 rounded border-border"
                        />
                        <div className="flex-1">
                          <CardTitle className="text-sm">{schema.tableName}</CardTitle>
                          <CardDescription>
                            {schema.recordCount.toLocaleString()} records
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-64 overflow-auto rounded-md border p-2">
                        <pre className="text-xs">
                          {JSON.stringify(schema.schema, null, 2)}
                        </pre>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {dataData.schemas && dataData.schemas.length > 1 && (
                  <div className="flex gap-2 text-xs">
                    <button
                      className="text-primary hover:underline"
                      onClick={() => setSelectedDatasets(new Set(dataData.schemas!.map((s: any) => s.id)))}
                    >
                      Select all
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <button
                      className="text-primary hover:underline"
                      onClick={() => setSelectedDatasets(new Set())}
                    >
                      Clear selection
                    </button>
                    {selectedDatasets.size > 0 && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">
                          {selectedDatasets.size} of {dataData.schemas.length} selected
                        </span>
                      </>
                    )}
                  </div>
                )}

                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    Showing {Math.min(dataData.limit || 0, dataData.data?.length || 0)} of {dataData.totalRecords || 0} records
                  </span>
                  {dataData.hasMore && <span>Load more...</span>}
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                  <Database className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
                  <p className="text-sm text-muted-foreground max-w-md mb-4">
                    {dataData?.message || 'Execute the pipeline to generate transformed data'}
                  </p>
                  {dataData?.hint && (
                    <p className="text-xs text-muted-foreground mb-4">
                      💡 {dataData.hint}
                    </p>
                  )}
                  <Button
                    onClick={() => executePipeline.mutate(pipelineId)}
                    disabled={!isActive || executePipeline.isPending}
                    className="mt-2"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    {executePipeline.isPending ? 'Executing...' : 'Execute Pipeline'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
